import {
    SelectContentCombobox,
    SelectItemCombobox,
    SelectTrigger,
    SelectValue,
} from "@/components/podkit/select/Select";
import { ComboboxProvider } from "@ariakit/react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { useCallback, useMemo, useState, startTransition, type FC } from "react";

export type FilterSelectProps = {
    initialValue?: string;
    items: Map<string, string>;
    placeholder: string;
    onChange?: (value: string) => void;
    className?: string;
    "data-testid"?: string;
};

export const FilterSelect: FC<FilterSelectProps> = ({
    initialValue: value,
    placeholder,
    items,
    onChange,
    className,
    "data-testid": testId,
}) => {
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");

    const onValueChange = useCallback(
        (newValue: string) => {
            const finalValue = newValue === "*" ? "" : newValue;
            if (onChange) {
                onChange(finalValue);
            }
        },
        [onChange],
    );

    const matches = useMemo(() => {
        // Only calculate matches when dropdown is open
        if (!open) return [];

        // Convert items to array only once when needed
        const itemsArray = Array.from(items.entries());

        if (!searchValue) return itemsArray;

        const words = searchValue.toLowerCase().split(" ");
        const filteredItems = itemsArray.filter(([, label]) => {
            const searchText = label.toLowerCase();
            return words.every((word) => searchText.includes(word));
        });

        // Ensure the selected item is always included in matches
        if (value && items.has(value)) {
            const selectedItem = [value, items.get(value)!] as [string, string];
            if (!filteredItems.some(([itemId]) => itemId === value)) {
                filteredItems.push(selectedItem);
            }
        }

        return filteredItems;
    }, [items, searchValue, value, open]);

    return (
        <SelectPrimitive.Root value={value || "*"} onValueChange={onValueChange} open={open} onOpenChange={setOpen}>
            <ComboboxProvider
                open={open}
                setOpen={setOpen}
                resetValueOnHide
                includesBaseElement={false}
                setValue={(value) => {
                    startTransition(() => {
                        setSearchValue(value.toLowerCase());
                    });
                }}
            >
                <SelectTrigger disabled={!items.size} className={className} loading={false} data-testid={testId}>
                    <span className="inline-block truncate">
                        <SelectValue placeholder={placeholder}>{value ? items.get(value) : placeholder}</SelectValue>
                    </span>
                </SelectTrigger>
                <SelectContentCombobox inputPlaceholder={`Search ${placeholder.toLowerCase()}`}>
                    {open && (
                        <>
                            <SelectItemCombobox key={"reset-item"} value={"*"} asChild>
                                <div className="flex w-full flex-row items-center justify-between pr-10">
                                    <div>{placeholder}</div>
                                </div>
                            </SelectItemCombobox>
                            {matches.map(([id, label]) => (
                                <SelectItemCombobox key={"item-" + id} value={id} asChild>
                                    <div className="flex w-full flex-row items-center justify-between pr-10">
                                        <div>{label}</div>
                                    </div>
                                </SelectItemCombobox>
                            ))}
                        </>
                    )}
                </SelectContentCombobox>
            </ComboboxProvider>
        </SelectPrimitive.Root>
    );
};
